import { extractApiItems } from './extract-api-items.js';
import { mapApiItemsToOptions } from './map-options-response.js';
import { buildOptionsUrl } from './build-options-url.js';

/**
 * Configuration for the API options loader factory.
 *
 * @typedef {Object} ApiOptionsLoaderConfig
 * @property {string} url - API endpoint path (e.g. '/api/roles').
 * @property {string} [queryParam] - Search query param name. Default: 'q'.
 * @property {string} [valueProp] - Value property in response items. Default: 'value'.
 * @property {string} [labelProp] - Label property in response items. Default: 'label'.
 * @property {string} [iconProp] - Response field to map to FieldOption.icon (or iconUrl when iconAs is 'image').
 * @property {string} [iconAs] - When 'image', maps iconProp to FieldOption.iconUrl instead of icon.
 * @property {Record<string, string>} [valuesParams] - Map of form field keys to query param names.
 *   On each load, matching ctx.values entries are sent as query params.
 *   Null, undefined, empty-string, and whitespace-only values are skipped.
 *   String values are trimmed before encoding.
 * @property {number} [limit] - Cap on rows per search/load. Sent as `limit=N`.
 *   Backend clamps 1..200; absent → no cap (per-caller opt-in).
 *   Lookup calls (createApiOptionsLookup) ignore this — selected-value hydration
 *   bypasses the cap.
 * @property {(response: unknown) => unknown[]} [extractItems] - Extract items array from response.
 *   Default: shared `extractApiItems` (array, then response.data if array, else []).
 */

/**
 * Creates an OptionLoader from an API endpoint configuration.
 * Empty-string values are filtered out (they conflict with placeholder/no-selection).
 *
 * @param {import('../../contracts/api.js').ApiClient} apiClient
 * @param {ApiOptionsLoaderConfig} config
 * @returns {import('../../contracts/forms.js').OptionLoader}
 */
export function createApiOptionsLoader(apiClient, config) {
  const {
    url,
    queryParam = 'q',
    valueProp = 'value',
    labelProp = 'label',
    iconProp,
    iconAs,
    valuesParams,
    limit,
    extractItems = extractApiItems,
  } = config;

  return async (ctx) => {
    const parts = /** @type {string[]} */ ([]);
    if (ctx.query) {
      parts.push(`${queryParam}=${encodeURIComponent(ctx.query)}`);
    }
    if (valuesParams) {
      for (const [fieldKey, paramName] of Object.entries(valuesParams)) {
        const raw = ctx.values[fieldKey];
        if (raw == null || raw === '') continue;
        if (Array.isArray(raw)) {
          for (const item of raw) {
            if (item == null) continue;
            const s = String(item).trim();
            if (s === '') continue;
            parts.push(`${paramName}=${encodeURIComponent(s)}`);
          }
        } else if (typeof raw === 'string') {
          const trimmed = raw.trim();
          if (trimmed !== '') parts.push(`${paramName}=${encodeURIComponent(trimmed)}`);
        } else {
          parts.push(`${paramName}=${encodeURIComponent(String(raw))}`);
        }
      }
    }
    if (typeof limit === 'number' && Number.isFinite(limit) && limit > 0) {
      parts.push(`limit=${encodeURIComponent(String(Math.floor(limit)))}`);
    }
    const finalUrl = buildOptionsUrl(url, parts);
    const response = await apiClient.get(finalUrl, { signal: ctx.signal });

    const items = extractItems(response);
    return mapApiItemsToOptions(items, { valueProp, labelProp, iconProp, iconAs });
  };
}
